import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "../../lib/api";
import { Btn, Empty, shortDate } from "./kit";

export function ThreadView({ messages = [], mineSender, onSend, title, subtitle }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      await onSend(body.trim());
      setBody("");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex h-full min-h-[420px] flex-col rounded-2xl border border-[#1E293B] bg-[#0F172A]/80" data-testid="thread-view">
      {title && (
        <div className="border-b border-[#1E293B] px-5 py-4">
          <p className="font-display text-xl font-bold uppercase text-white">{title}</p>
          {subtitle && <p className="text-sm text-slate-400">{subtitle}</p>}
        </div>
      )}
      <div ref={boxRef} className="flex-1 space-y-3 overflow-y-auto p-5" style={{ maxHeight: 460 }}>
        {messages.length === 0 && <Empty title="No messages yet" text="Start the conversation below." />}
        {messages.map((m, i) => {
          const mine = m.sender === mineSender;
          return (
            <div key={m.id ?? i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div data-testid="thread-message" className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "rounded-br-sm bg-[#39FF14] text-[#0A0E1A]" : "rounded-bl-sm bg-[#162032] text-slate-100"}`}>
                <p className="whitespace-pre-wrap">{m.body}</p>
                <p className={`mt-1 font-mono-gd text-[10px] uppercase tracking-wider ${mine ? "text-[#0A0E1A]/70" : "text-slate-400"}`}>
                  {m.sender_name ?? m.sender} · {shortDate(m.created_at)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={submit} className="no-print flex gap-2 border-t border-[#1E293B] p-3">
        <input
          data-testid="thread-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write a message…"
          className="flex-1 rounded-full border border-[#1E293B] bg-[#0A0E1A] px-4 py-2.5 text-sm text-white outline-none placeholder:text-slate-500 focus:border-[#39FF14]"
        />
        <Btn data-testid="thread-send-button" type="submit" disabled={sending || !body.trim()}><Send className="h-4 w-4" />Send</Btn>
      </form>
    </div>
  );
}
